import React, { createContext, useContext, useMemo } from 'react';
import useSWR from 'swr';
import { fetcher } from '../../services/fetcher';
import {
  buildListQuery,
  ListQueryOptions,
  normalizePaginatedListResponse,
  PaginatedApiResponse,
} from '../../services/paginatedList';
import type { SpareRequisition } from '../../modules/spares/requisitions/useData';

export type {
  SpareRequisition,
  SpareRequisitionLine,
  RequisitionSaveStatus,
  SpareRequisitionTemplate,
  SpareRequisitionTemplateLine,
} from '../../modules/spares/requisitions/useData';

export { useSpareRequisition } from '../../modules/spares/requisitions/useData';

const API_LIST = '/spare-requisitions';

const VesselSpareRequisitionsListContext = createContext<number | null>(null);

export function VesselSpareRequisitionsListProvider(props: { vesselId: number; children: React.ReactNode }) {
  const { vesselId, children } = props;
  return React.createElement(VesselSpareRequisitionsListContext.Provider, { value: vesselId }, children);
}

/**
 * List hook for GenericListPage: scopes spare requisitions to the vessel from context via server filter `vesselId`.
 */
export function useVesselSpareRequisitionsListData(
  searchParams?: string,
  page = 1,
  limit = 25,
  options?: ListQueryOptions,
) {
  const vesselId = useContext(VesselSpareRequisitionsListContext);
  if (vesselId == null || !Number.isFinite(vesselId)) {
    throw new Error('useVesselSpareRequisitionsListData requires VesselSpareRequisitionsListProvider with a numeric vesselId');
  }

  const mergedOptions = useMemo((): ListQueryOptions => {
    const prev = options?.filters?.filters ?? {};
    return {
      ...options,
      filters: {
        ...options?.filters,
        filters: {
          ...prev,
          vesselId: [String(vesselId)],
        },
      },
    };
  }, [vesselId, options]);

  const q = buildListQuery(searchParams, page, limit, mergedOptions);
  const { data, error, isLoading, mutate } = useSWR<PaginatedApiResponse<SpareRequisition> | SpareRequisition[]>(
    `${API_LIST}${q}`,
    fetcher,
    { revalidateOnFocus: true },
  );
  const normalized = normalizePaginatedListResponse<SpareRequisition>(data, page, limit);
  return {
    data: normalized.data,
    clients: normalized.data,
    total: normalized.total,
    page: normalized.page,
    limit: normalized.limit,
    isLoading,
    isError: error,
    mutate,
  };
}
